import { useMemo, useState } from "react";
import { Plus, Loader2, Save, FileUp, Search, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyPledgee = () => ({ kind: "person", name: "", id_number: "", email: "", contact_name: "", contact_email: "", verified: false });
const EMPTY = { shareholder_id: "", shares: "", share_class: "", note: "", date: "" };

export function NewPledgeDialog({ companyId, rows = [], classes = [], onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [pledgees, setPledgees] = useState([emptyPledgee()]);
  const [file, setFile] = useState(null);
  const [proffIdx, setProffIdx] = useState(-1);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const classNames = classes.map((c) => c.name).filter(Boolean);
  const selected = useMemo(() => rows.find((r) => r.id === form.shareholder_id) || null, [rows, form.shareholder_id]);

  const reset = () => { setForm(EMPTY); setPledgees([emptyPledgee()]); setFile(null); setSaving(false); setProffIdx(-1); };
  const onOpenChange = (o) => { if (o) reset(); setOpen(o); };

  const updP = (i, patch) => setPledgees((list) => list.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const addP = () => setPledgees((list) => [...list, emptyPledgee()]);
  const removeP = (i) => setPledgees((list) => (list.length > 1 ? list.filter((_, idx) => idx !== i) : list));

  const fetchProffName = async (i, org) => {
    const d = (org || "").replace(/\D/g, "");
    if (d.length !== 9) return toast.error("Skriv inn org.nr (9 siffer) først");
    setProffIdx(i);
    try {
      const { data } = await api.get(`/proff/company/${d}`);
      if (data?.name) updP(i, { name: data.name, verified: true });
    } catch {
      // stille – navn kan skrives manuelt
    } finally {
      setProffIdx(-1);
    }
  };

  const save = async () => {
    if (!form.shareholder_id) return toast.error("Velg aksjonæren som pantsetter aksjer");
    const n = Number(form.shares);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Antall aksjer må være større enn 0");
    const clean = pledgees.filter((p) => p.name.trim());
    if (clean.length === 0) return toast.error("Legg til minst én panthaver med navn");
    for (const p of clean) {
      if (p.kind === "selskap" && (!p.contact_name.trim() || !p.contact_email.trim()))
        return toast.error(`Panthaver-selskap «${p.name}» må ha en kontaktperson med navn og e-post`);
    }
    if (!file) return toast.error("Last opp pantavtalen");

    const payload = clean.map((p) => ({
      kind: p.kind, name: p.name.trim(), id_number: p.id_number || "",
      email: p.kind === "person" ? (p.email || "").trim() : "",
      contact_name: p.kind === "selskap" ? p.contact_name.trim() : "",
      contact_email: p.kind === "selskap" ? p.contact_email.trim() : "",
    }));

    const fd = new FormData();
    fd.append("file", file);
    fd.append("shareholder_id", form.shareholder_id);
    fd.append("shares", String(Math.round(n)));
    fd.append("share_class", form.share_class || "");
    fd.append("pledgees", JSON.stringify(payload));
    fd.append("note", form.note || "");
    fd.append("date", form.date || "");
    setSaving(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/pledges`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data?.notified ? `Pant registrert – varsel sendt til ${data.notified} panthaver(e)` : "Pant registrert i aksjeeierboka");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid="new-pledge-btn">
          <Plus className="h-4 w-4" /> Registrer pant
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="new-pledge-dialog">
        <DialogHeader>
          <DialogTitle>Registrer pant i aksjer</DialogTitle>
          <DialogDescription>
            Registrer pant på en aksjonærs aksjer, hvem som har tatt pant (én eller flere panthavere), og last opp pantavtalen.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs">Aksjonær som pantsetter</Label>
            <Select value={form.shareholder_id} onValueChange={(v) => { set("shareholder_id", v); set("share_class", ""); }}>
              <SelectTrigger className="mt-1.5" data-testid="pledge-shareholder-select"><SelectValue placeholder="Velg aksjonær" /></SelectTrigger>
              <SelectContent>
                {rows.map((r) => (
                  <SelectItem key={r.id} value={r.id} data-testid={`pledge-shareholder-${r.id}`}>{r.name} · {r.shares} aksjer</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Antall aksjer i pant</Label>
              <Input type="number" inputMode="numeric" className="mt-1.5" data-testid="pledge-shares-input" value={form.shares} onChange={(e) => set("shares", e.target.value)} placeholder="0" />
            </div>
            <div>
              <Label className="text-xs">Aksjeklasse</Label>
              {classNames.length ? (
                <Select value={form.share_class || "__all__"} onValueChange={(v) => set("share_class", v === "__all__" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="pledge-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Alle klasser</SelectItem>
                    {classNames.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input className="mt-1.5" value={form.share_class} onChange={(e) => set("share_class", e.target.value)} placeholder="Alle klasser" />
              )}
            </div>
          </div>
          {selected ? (
            <p className="-mt-2 text-[11px] text-muted-foreground" data-testid="pledge-available-hint">
              {selected.name} eier {selected.shares} aksjer totalt.
            </p>
          ) : null}

          <div className="space-y-3">
            <Label className="text-xs">Panthavere (hvem har tatt pant)</Label>
            {pledgees.map((p, i) => {
              const isCompany = p.kind === "selskap";
              return (
                <div key={i} className="rounded-lg border border-border/60 bg-muted/20 p-3" data-testid={`pledgee-block-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Panthaver {i + 1}</span>
                    {pledgees.length > 1 ? (
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" data-testid={`pledgee-remove-${i}`} onClick={() => removeP(i)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>

                  <div className="mt-2 flex gap-1.5 rounded-lg border border-border p-1">
                    {[["person", "Person"], ["selskap", "Selskap"]].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`pledgee-kind-${i}-${v}`}
                        onClick={() => updP(i, { kind: v, id_number: "", verified: false })}
                        className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${p.kind === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>

                  {isCompany ? (
                    <div className="mt-2">
                      <Label className="text-xs">Org.nr (valgfritt – henter firmanavn)</Label>
                      <Input className="mt-1.5" inputMode="numeric" data-testid={`pledgee-org-${i}`} value={p.id_number} onChange={(e) => updP(i, { id_number: e.target.value, verified: false })} placeholder="9 siffer" />
                    </div>
                  ) : null}

                  <div className="mt-2">
                    <Label className="text-xs">Navn på panthaver</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input data-testid={`pledgee-name-${i}`} value={p.name} onChange={(e) => updP(i, { name: e.target.value, verified: false })} placeholder={isCompany ? "Firmanavn" : "Navn"} />
                      {isCompany ? (
                        <Button type="button" variant="outline" className="shrink-0 gap-1.5" data-testid={`pledgee-proff-${i}`} disabled={proffIdx === i} onClick={() => fetchProffName(i, p.id_number)}>
                          {proffIdx === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Hent
                        </Button>
                      ) : null}
                    </div>
                    {p.verified ? (
                      <p className="mt-1 flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid={`pledgee-verified-${i}`}><Check className="h-3.5 w-3.5" /> Bekreftet mot Proff</p>
                    ) : null}
                  </div>

                  {isCompany ? (
                    <div className="mt-2 space-y-2">
                      <Label className="text-xs font-medium">Kontaktperson for selskapet (påkrevd)</Label>
                      <Input data-testid={`pledgee-contact-name-${i}`} value={p.contact_name} onChange={(e) => updP(i, { contact_name: e.target.value })} placeholder="Navn på kontaktperson" />
                      <Input type="email" data-testid={`pledgee-contact-email-${i}`} value={p.contact_email} onChange={(e) => updP(i, { contact_email: e.target.value })} placeholder="E-post" />
                    </div>
                  ) : (
                    <div className="mt-2">
                      <Label className="text-xs">E-post til panthaver (valgfritt)</Label>
                      <Input type="email" className="mt-1.5" data-testid={`pledgee-email-${i}`} value={p.email} onChange={(e) => updP(i, { email: e.target.value })} placeholder="For varsel med lenke til avtalen" />
                    </div>
                  )}
                </div>
              );
            })}
            <Button type="button" variant="outline" size="sm" className="w-full gap-1.5" data-testid="add-pledgee-btn" onClick={addP}>
              <Plus className="h-4 w-4" /> Legg til panthaver
            </Button>
          </div>

          <div>
            <Label className="text-xs">Pantavtale (påkrevd)</Label>
            <label className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm hover:bg-muted/40" data-testid="pledge-file-label">
              <FileUp className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate text-muted-foreground">{file ? file.name : "Velg avtalefil (PDF) …"}</span>
              <input type="file" accept="application/pdf,.pdf,image/*" className="hidden" data-testid="pledge-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Dato (valgfritt)</Label>
              <Input type="date" className="mt-1.5" data-testid="pledge-date-input" value={form.date} onChange={(e) => set("date", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Merknad (valgfritt)</Label>
            <Textarea className="mt-1.5" rows={2} data-testid="pledge-note-input" value={form.note} onChange={(e) => set("note", e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Avbryt</Button>
          <Button className="gap-2" data-testid="pledge-save-btn" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Registrer pant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
